import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import CountrySearchSelect from '../common/CountrySearchSelect';
import JobTag from './JobTag';
import { useAuth } from '../../context/AuthContext';
import { useCountries } from '../../hooks/useCountries';
import { apiGet, apiPost } from '../../services/api';

const BRAND_AVATAR_COLORS = [
  'var(--rwm-cyan)',
  'var(--rwm-blue)',
  'var(--rwm-amber)',
  'var(--rwm-slate)',
  'var(--rwm-salary)',
  'var(--rwm-dark)',
];

function currencySymbol(code) {
  if (code === 'USD') return '$';
  if (code === 'EUR') return '€';
  if (code === 'GBP') return '£';
  return `${code} `;
}

function formatSalaryBrowse(min, max, currency, period) {
  const sym = currencySymbol(currency);
  const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  const a = nf.format(Number(min));
  const b = nf.format(Number(max));
  const periodPart =
    {
      annual: '/ year',
      monthly: '/ month',
      hourly: '/ hour',
      yearly: '/ year',
      project: '',
    }[period] || '/ month';
  return `${sym}${a}–${sym}${b} ${periodPart}`.trim();
}

function timeAgo(isoString) {
  if (!isoString) return '';
  const then = new Date(isoString);
  const now = new Date();
  const diffMs = now - then;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 1) return 'Today';
  if (days === 1) return '1 day ago';
  if (days >= 2 && days <= 6) return `${days} days ago`;
  if (days >= 7 && days <= 13) return '1 week ago';
  const weeks = Math.floor(days / 7);
  if (days >= 14 && days <= 29) return `${weeks} weeks ago`;
  return '1 month ago';
}

function avatarColor(name) {
  const ch = (name && name.trim()[0]) || 'A';
  return BRAND_AVATAR_COLORS[ch.charCodeAt(0) % BRAND_AVATAR_COLORS.length];
}

function formatEmploymentLabel(value) {
  if (!value) return '';
  return String(value)
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}

function formatSeniorityTags(levels) {
  if (!levels || !Array.isArray(levels) || levels.length === 0) return null;
  const labels = {
    entry: 'Entry Level',
    mid: 'Mid-Level',
    senior: 'Senior',
    lead: 'Lead / Principal',
  };
  return levels.map((l) => labels[l] || l).join(' / ');
}

const EMPLOYMENT_TYPE_ORDER = ['full-time', 'part-time', 'contract', 'freelance'];

const EMPTY_EMPLOYMENT_SELECTION = {
  'full-time': false,
  'part-time': false,
  contract: false,
  freelance: false,
};

const SALARY_THRESHOLD_VALUES = ['50000', '75000', '100000', '150000'];

const SENIORITY_VALUES = ['entry', 'mid', 'senior', 'lead'];

const LOCATION_VALUES = ['Worldwide', 'Europe only', 'US only', 'Americas', 'EMEA', 'APAC'];

const LOCATION_FILTER_TEST_IDS = {
  Worldwide: 'rwm-filter-location-worldwide',
  'Europe only': 'rwm-filter-location-europe',
  'US only': 'rwm-filter-location-us',
  Americas: 'rwm-filter-location-americas',
  EMEA: 'rwm-filter-location-emea',
  APAC: 'rwm-filter-location-apac',
};

const POSTED_PRESET_KEYS = ['24h', '7d', '30d', '90d'];

function formatLocalYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function localTodayYmd() {
  return formatLocalYmd(new Date());
}

function localDaysAgoYmd(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return formatLocalYmd(d);
}

function postedPresetToDateFrom(preset) {
  if (preset === '24h') return localTodayYmd();
  if (preset === '7d') return localDaysAgoYmd(7);
  if (preset === '30d') return localDaysAgoYmd(30);
  if (preset === '90d') return localDaysAgoYmd(90);
  return '';
}

function employmentSelectionFromUrlParam(raw) {
  const next = { ...EMPTY_EMPLOYMENT_SELECTION };
  if (!raw || !String(raw).trim()) return next;
  String(raw)
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((v) => EMPLOYMENT_TYPE_ORDER.includes(v))
    .forEach((v) => {
      next[v] = true;
    });
  return next;
}

const EMPTY_SENIORITY_SELECTION = {
  entry: false,
  mid: false,
  senior: false,
  lead: false,
};

function senioritySelectionFromUrlParam(raw) {
  const next = { ...EMPTY_SENIORITY_SELECTION };
  if (!raw || !String(raw).trim()) return next;
  String(raw)
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((v) => SENIORITY_VALUES.includes(v))
    .forEach((v) => {
      next[v] = true;
    });
  return next;
}

const EMPTY_LOCATION_SELECTION = Object.fromEntries(LOCATION_VALUES.map((v) => [v, false]));

function locationSelectionFromUrlParam(raw) {
  const next = { ...EMPTY_LOCATION_SELECTION };
  if (!raw || !String(raw).trim()) return next;
  String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter((v) => LOCATION_VALUES.includes(v))
    .forEach((v) => {
      next[v] = true;
    });
  return next;
}

function parseSkillsFromParam(raw) {
  if (!raw || !String(raw).trim()) return [];
  return String(raw)
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((id) => Number.isFinite(id) && id > 0);
}

function SkillCategoryGroup({ category, skills, selectedIds, onToggle }) {
  const selectedInCategory = skills.filter((s) => selectedIds.includes(s.id)).length;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (selectedInCategory > 0) setOpen(true);
  }, [selectedInCategory]);

  return (
    <div className="mb-2">
      <button
        type="button"
        className="rwm-skill-category__header w-100 text-start border-0 bg-transparent"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="me-1">{open ? '▾' : '▸'}</span>
        {category}
        {selectedInCategory > 0 ? (
          <span className="ms-1 badge bg-primary rounded-pill" style={{ fontSize: '0.65rem' }}>
            {selectedInCategory}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="ps-2 mt-1">
          {skills.map((skill) => (
            <Form.Check
              key={skill.id}
              type="checkbox"
              id={`rwm-browse-skill-${skill.id}`}
              label={skill.name}
              checked={selectedIds.includes(skill.id)}
              onChange={() => onToggle(skill.id)}
              className="small mb-1"
              data-testid={`rwm-browse-skill-${skill.id}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function buildFiltersFromAppliedParams({
  keyword,
  category,
  categoryId,
  employmentType,
  salaryThreshold,
  seniority,
  locationEligibility,
  locationCountry,
  posted,
  dateFrom,
  dateTo,
  skills = [],
}) {
  const filters = {};
  if (keyword) filters.keyword = keyword;
  if (categoryId) filters.category_id = categoryId;
  else if (category) filters.category = category;
  if (employmentType) filters.employment_type = employmentType;
  if (salaryThreshold) filters.salary_min_threshold = salaryThreshold;
  if (seniority) filters.seniority_level = seniority;
  if (locationCountry) filters.location_country = locationCountry;
  else if (locationEligibility) filters.location_eligibility = locationEligibility;
  if (posted) filters.posted = posted;
  else {
    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
  }
  if (skills && skills.length > 0) filters.skills = skills;
  return filters;
}

function buildJobsApiPath(params) {
  const u = new URLSearchParams();
  if (params.keyword) u.set('keyword', params.keyword);
  if (params.category_id) u.set('category_id', String(params.category_id));
  else if (params.category) u.set('category', params.category);
  if (params.employment_type) u.set('employment_type', params.employment_type);
  if (params.salary_min_threshold) u.set('salary_min_threshold', String(params.salary_min_threshold));
  if (params.seniority_level) u.set('seniority_level', params.seniority_level);
  if (params.location_eligibility) u.set('location_eligibility', params.location_eligibility);
  if (params.location_country) u.set('location_country', params.location_country);
  if (params.date_from) u.set('date_from', params.date_from);
  if (params.date_to) u.set('date_to', params.date_to);
  if (params.page && params.page > 1) u.set('page', String(params.page));
  if (params.skills && params.skills.length > 0) {
    params.skills.forEach((id) => u.append('skills[]', String(id)));
  }
  const qs = u.toString();
  return `/api/v1/jobs${qs ? `?${qs}` : ''}`;
}

function BrowseJobs() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isCandidate = user?.role === 'candidate';
  const [searchParams, setSearchParams] = useSearchParams();
  const keywordParam = searchParams.get('keyword') || '';
  const categoryParam = searchParams.get('category') || '';
  const categoryIdParam = searchParams.get('category_id') || '';
  const pageParam = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const categoryIdForRequest = useMemo(() => {
    const id = parseInt(categoryIdParam, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [categoryIdParam]);

  const categorySlugForRequest = categoryIdForRequest ? '' : categoryParam;
  const employmentTypeUrlRaw = searchParams.get('employment_type') || '';
  const salaryMinThresholdParam = searchParams.get('salary_min_threshold') || '';
  const seniorityLevelUrlRaw = searchParams.get('seniority_level') || '';
  const locationEligibilityUrlRaw = searchParams.get('location_eligibility') || '';
  const locationCountryUrlRaw = searchParams.get('location_country') || '';
  const postedUrlParam = searchParams.get('posted') || '';
  const { countries, loading: countriesLoading } = useCountries();
  const dateFromUrlParam = searchParams.get('date_from') || '';
  const dateToUrlParam = searchParams.get('date_to') || '';
  const skillsParam = searchParams.get('skills') || '';

  const [appliedSkills, setAppliedSkills] = useState(() => parseSkillsFromParam(skillsParam));
  const [draftSkills, setDraftSkills] = useState(() => parseSkillsFromParam(skillsParam));
  const [skillsData, setSkillsData] = useState({});
  const [skillSearch, setSkillSearch] = useState('');

  const employmentTypeForRequest = useMemo(() => {
    if (!employmentTypeUrlRaw.trim()) return '';
    const tokens = employmentTypeUrlRaw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const ordered = EMPLOYMENT_TYPE_ORDER.filter((t) => tokens.includes(t));
    return ordered.length ? ordered.join(',') : '';
  }, [employmentTypeUrlRaw]);

  const salaryThresholdForRequest = SALARY_THRESHOLD_VALUES.includes(salaryMinThresholdParam)
    ? salaryMinThresholdParam
    : '';

  const seniorityForRequest = useMemo(() => {
    if (!seniorityLevelUrlRaw.trim()) return '';
    const tokens = seniorityLevelUrlRaw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const ordered = SENIORITY_VALUES.filter((t) => tokens.includes(t));
    return ordered.length ? ordered.join(',') : '';
  }, [seniorityLevelUrlRaw]);

  const locationForRequest = useMemo(() => {
    if (locationCountryUrlRaw.trim()) return '';
    if (!locationEligibilityUrlRaw.trim()) return '';
    const parts = locationEligibilityUrlRaw.split(',').map((s) => s.trim()).filter(Boolean);
    const ordered = LOCATION_VALUES.filter((t) => parts.includes(t));
    return ordered.length ? ordered.join(',') : '';
  }, [locationEligibilityUrlRaw, locationCountryUrlRaw]);

  const locationCountryForRequest = useMemo(() => locationCountryUrlRaw.trim(), [locationCountryUrlRaw]);

  const { dateFromForRequest, dateToForRequest } = useMemo(() => {
    if (POSTED_PRESET_KEYS.includes(postedUrlParam)) {
      return {
        dateFromForRequest: postedPresetToDateFrom(postedUrlParam),
        dateToForRequest: '',
      };
    }
    if (dateFromUrlParam || dateToUrlParam) {
      if (dateFromUrlParam && dateToUrlParam && dateFromUrlParam > dateToUrlParam) {
        return { dateFromForRequest: '', dateToForRequest: '' };
      }
      return { dateFromForRequest: dateFromUrlParam, dateToForRequest: dateToUrlParam };
    }
    return { dateFromForRequest: '', dateToForRequest: '' };
  }, [postedUrlParam, dateFromUrlParam, dateToUrlParam]);

  const [draftKeyword, setDraftKeyword] = useState(keywordParam);
  const [draftCategory, setDraftCategory] = useState(categoryParam);
  const [draftEmploymentTypes, setDraftEmploymentTypes] = useState(() =>
    employmentSelectionFromUrlParam(employmentTypeUrlRaw)
  );
  const [draftSalaryThreshold, setDraftSalaryThreshold] = useState(() =>
    SALARY_THRESHOLD_VALUES.includes(salaryMinThresholdParam) ? salaryMinThresholdParam : ''
  );
  const [draftSenioritySelection, setDraftSenioritySelection] = useState(() =>
    senioritySelectionFromUrlParam(seniorityLevelUrlRaw)
  );
  const [draftLocationSelection, setDraftLocationSelection] = useState(() =>
    locationSelectionFromUrlParam(locationEligibilityUrlRaw)
  );
  const [draftSpecificCountryMode, setDraftSpecificCountryMode] = useState(
    () => Boolean(locationCountryUrlRaw.trim())
  );
  const [draftSelectedCountry, setDraftSelectedCountry] = useState(
    () => locationCountryUrlRaw.trim()
  );
  const [draftPostedPreset, setDraftPostedPreset] = useState(() => {
    if (POSTED_PRESET_KEYS.includes(postedUrlParam)) return postedUrlParam;
    if (dateFromUrlParam || dateToUrlParam) return 'custom';
    return '';
  });
  const [draftCustomDateFrom, setDraftCustomDateFrom] = useState(() => dateFromUrlParam || '');
  const [draftCustomDateTo, setDraftCustomDateTo] = useState(() => dateToUrlParam || '');
  const [postedDateError, setPostedDateError] = useState('');

  const [categories, setCategories] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    per_page: 20,
    current_page: 1,
    last_page: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showSaveSearch, setShowSaveSearch] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [saveSearchSaving, setSaveSearchSaving] = useState(false);
  const [saveSearchError, setSaveSearchError] = useState('');
  const [saveSearchSuccess, setSaveSearchSuccess] = useState(false);

  const hasActiveFilters = useMemo(() => {
    const filters = buildFiltersFromAppliedParams({
      keyword: keywordParam,
      category: categorySlugForRequest,
      categoryId: categoryIdForRequest,
      employmentType: employmentTypeForRequest,
      salaryThreshold: salaryThresholdForRequest,
      seniority: seniorityForRequest,
      locationEligibility: locationForRequest,
      locationCountry: locationCountryForRequest,
      posted: POSTED_PRESET_KEYS.includes(postedUrlParam) ? postedUrlParam : '',
      dateFrom: dateFromForRequest,
      dateTo: dateToForRequest,
      skills: appliedSkills,
    });
    return Object.keys(filters).length > 0;
  }, [
    keywordParam,
    categorySlugForRequest,
    categoryIdForRequest,
    employmentTypeForRequest,
    salaryThresholdForRequest,
    seniorityForRequest,
    locationForRequest,
    locationCountryForRequest,
    postedUrlParam,
    dateFromForRequest,
    dateToForRequest,
    appliedSkills,
  ]);

  const jobListRef = useRef(null);
  const isPaginationChange = useRef(false);

  useEffect(() => {
    setDraftKeyword(keywordParam);
    if (categoryIdParam && categories.length > 0) {
      const id = parseInt(categoryIdParam, 10);
      const match = categories.find((c) => Number(c.id) === id);
      setDraftCategory(match ? match.slug : '');
    } else {
      setDraftCategory(categoryParam);
    }
    setDraftEmploymentTypes(employmentSelectionFromUrlParam(employmentTypeUrlRaw));
    setDraftSalaryThreshold(
      SALARY_THRESHOLD_VALUES.includes(salaryMinThresholdParam) ? salaryMinThresholdParam : ''
    );
    setDraftSenioritySelection(senioritySelectionFromUrlParam(seniorityLevelUrlRaw));
    setDraftLocationSelection(locationSelectionFromUrlParam(locationEligibilityUrlRaw));
    setDraftSpecificCountryMode(Boolean(locationCountryUrlRaw.trim()));
    setDraftSelectedCountry(locationCountryUrlRaw.trim());
    if (POSTED_PRESET_KEYS.includes(postedUrlParam)) {
      setDraftPostedPreset(postedUrlParam);
      setDraftCustomDateFrom('');
      setDraftCustomDateTo('');
    } else if (dateFromUrlParam || dateToUrlParam) {
      setDraftPostedPreset('custom');
      setDraftCustomDateFrom(dateFromUrlParam);
      setDraftCustomDateTo(dateToUrlParam);
    } else {
      setDraftPostedPreset('');
      setDraftCustomDateFrom('');
      setDraftCustomDateTo('');
    }
    const parsedSkills = parseSkillsFromParam(skillsParam);
    setDraftSkills(parsedSkills);
    setAppliedSkills(parsedSkills);
  }, [
    keywordParam,
    categoryParam,
    categoryIdParam,
    categories,
    employmentTypeUrlRaw,
    salaryMinThresholdParam,
    seniorityLevelUrlRaw,
    locationEligibilityUrlRaw,
    locationCountryUrlRaw,
    postedUrlParam,
    dateFromUrlParam,
    dateToUrlParam,
    skillsParam,
  ]);

  useEffect(() => {
    let cancelled = false;
    async function loadSkills() {
      try {
        const res = await apiGet('/api/v1/skills');
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) {
          setSkillsData(json.data && typeof json.data === 'object' ? json.data : {});
        }
      } catch {
        if (!cancelled) setSkillsData({});
      }
    }
    loadSkills();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadCategories() {
      try {
        const res = await apiGet('/api/v1/categories');
        if (!res.ok) throw new Error('categories');
        const json = await res.json();
        if (!cancelled && Array.isArray(json.data)) {
          setCategories(json.data);
        }
      } catch {
        if (!cancelled) setCategories([]);
      }
    }
    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const path = buildJobsApiPath({
        keyword: keywordParam,
        category: categorySlugForRequest,
        category_id: categoryIdForRequest,
        page: pageParam,
        employment_type: employmentTypeForRequest,
        salary_min_threshold: salaryThresholdForRequest,
        seniority_level: seniorityForRequest,
        location_eligibility: locationForRequest,
        location_country: locationCountryForRequest,
        date_from: dateFromForRequest,
        date_to: dateToForRequest,
        skills: appliedSkills,
      });
      const res = await apiGet(path, isCandidate ? token : null);
      if (!res.ok) throw new Error('jobs');
      const json = await res.json();
      const nextJobs = Array.isArray(json.data) ? json.data : [];
      const nextMeta = {
        total: json.meta?.total ?? 0,
        per_page: json.meta?.per_page ?? 20,
        current_page: json.meta?.current_page ?? 1,
        last_page: Math.max(1, json.meta?.last_page ?? 1),
      };
      setJobs(nextJobs);
      setMeta(nextMeta);
      if (isPaginationChange.current) {
        setTimeout(() => {
          jobListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          isPaginationChange.current = false;
        }, 0);
      }
    } catch {
      setError(true);
      setJobs([]);
      isPaginationChange.current = false;
    } finally {
      setLoading(false);
    }
  }, [
    keywordParam,
    categorySlugForRequest,
    categoryIdForRequest,
    pageParam,
    employmentTypeForRequest,
    salaryThresholdForRequest,
    seniorityForRequest,
    locationForRequest,
    locationCountryForRequest,
    dateFromForRequest,
    dateToForRequest,
    appliedSkills,
    isCandidate,
    token,
  ]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    setShowSaveSearch(false);
    setSaveSearchName('');
    setSaveSearchError('');
    setSaveSearchSuccess(false);
  }, [searchParams]);

  const handleSaveSearch = async () => {
    if (!token || !saveSearchName.trim()) return;
    setSaveSearchSaving(true);
    setSaveSearchError('');
    setSaveSearchSuccess(false);
    try {
      const filters = buildFiltersFromAppliedParams({
        keyword: keywordParam,
        category: categorySlugForRequest,
        categoryId: categoryIdForRequest,
        employmentType: employmentTypeForRequest,
        salaryThreshold: salaryThresholdForRequest,
        seniority: seniorityForRequest,
        locationEligibility: locationForRequest,
        locationCountry: locationCountryForRequest,
        posted: POSTED_PRESET_KEYS.includes(postedUrlParam) ? postedUrlParam : '',
        dateFrom: dateFromForRequest,
        dateTo: dateToForRequest,
        skills: appliedSkills,
      });
      const res = await apiPost(
        '/api/v1/candidate/saved-searches',
        { name: saveSearchName.trim(), filters },
        token
      );
      if (res.status === 401) {
        setSaveSearchError('Please log in to save searches.');
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg =
          data?.errors?.searches?.[0] ||
          data?.errors?.filters?.[0] ||
          data?.errors?.name?.[0] ||
          'Could not save search.';
        setSaveSearchError(msg);
        return;
      }
      setSaveSearchSuccess(true);
      setShowSaveSearch(false);
      setSaveSearchName('');
    } catch {
      setSaveSearchError('Could not save search.');
    } finally {
      setSaveSearchSaving(false);
    }
  };

  const handleApply = () => {
    if (draftPostedPreset === 'custom') {
      if (draftCustomDateFrom && draftCustomDateTo && draftCustomDateFrom > draftCustomDateTo) {
        setPostedDateError('Start date must be before end date');
        return;
      }
    }
    setPostedDateError('');

    const next = new URLSearchParams();
    if (draftKeyword.trim()) next.set('keyword', draftKeyword.trim());
    if (draftCategory) {
      const cat = categories.find((c) => c.slug === draftCategory);
      if (cat) {
        next.set('category_id', String(cat.id));
      } else {
        next.set('category', draftCategory);
      }
    }
    const employmentList = EMPLOYMENT_TYPE_ORDER.filter((k) => draftEmploymentTypes[k]);
    if (employmentList.length) next.set('employment_type', employmentList.join(','));
    if (draftSalaryThreshold) next.set('salary_min_threshold', draftSalaryThreshold);
    const seniorityList = SENIORITY_VALUES.filter((k) => draftSenioritySelection[k]);
    if (seniorityList.length) next.set('seniority_level', seniorityList.join(','));
    if (draftSpecificCountryMode && draftSelectedCountry) {
      next.set('location_country', draftSelectedCountry);
    } else {
      const locationList = LOCATION_VALUES.filter((k) => draftLocationSelection[k]);
      if (locationList.length) next.set('location_eligibility', locationList.join(','));
    }
    if (POSTED_PRESET_KEYS.includes(draftPostedPreset)) {
      next.set('posted', draftPostedPreset);
    } else if (draftPostedPreset === 'custom') {
      if (draftCustomDateFrom) next.set('date_from', draftCustomDateFrom);
      if (draftCustomDateTo) next.set('date_to', draftCustomDateTo);
    }
    if (draftSkills.length > 0) {
      next.set('skills', draftSkills.join(','));
    } else {
      next.delete('skills');
    }
    setAppliedSkills(draftSkills);
    setSearchParams(next);
  };

  const handleClear = () => {
    setDraftKeyword('');
    setDraftCategory('');
    setDraftEmploymentTypes({ ...EMPTY_EMPLOYMENT_SELECTION });
    setDraftSalaryThreshold('');
    setDraftSenioritySelection({ ...EMPTY_SENIORITY_SELECTION });
    setDraftLocationSelection({ ...EMPTY_LOCATION_SELECTION });
    setDraftSpecificCountryMode(false);
    setDraftSelectedCountry('');
    setDraftPostedPreset('');
    setDraftCustomDateFrom('');
    setDraftCustomDateTo('');
    setDraftSkills([]);
    setAppliedSkills([]);
    setSkillSearch('');
    setPostedDateError('');
    setSearchParams({});
  };

  const changePostedPreset = (value) => {
    setPostedDateError('');
    setDraftPostedPreset(value);
    if (value !== 'custom') {
      setDraftCustomDateFrom('');
      setDraftCustomDateTo('');
    }
  };

  const toggleEmploymentType = (value) => {
    setDraftEmploymentTypes((prev) => ({ ...prev, [value]: !prev[value] }));
  };

  const toggleSeniority = (value) => {
    setDraftSenioritySelection((prev) => ({ ...prev, [value]: !prev[value] }));
  };

  const toggleLocation = (value) => {
    if (draftSpecificCountryMode) return;
    setDraftLocationSelection((prev) => ({ ...prev, [value]: !prev[value] }));
  };

  const enableSpecificCountryMode = () => {
    setDraftSpecificCountryMode(true);
    setDraftLocationSelection({ ...EMPTY_LOCATION_SELECTION });
  };

  const disableSpecificCountryMode = () => {
    setDraftSpecificCountryMode(false);
    setDraftSelectedCountry('');
  };

  const changeDraftCountry = (name) => {
    setDraftSelectedCountry(name);
    if (name) {
      setDraftSpecificCountryMode(true);
      setDraftLocationSelection({ ...EMPTY_LOCATION_SELECTION });
    }
  };

  const goToPage = (p) => {
    isPaginationChange.current = true;
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setSearchParams(next);
  };

  const resultsLine = () => {
    const x = meta.total;
    if (keywordParam.trim()) {
      return `Showing ${x} remote jobs for '${keywordParam.trim()}'`;
    }
    return `Showing ${x} remote jobs`;
  };

  const pageNumbers = useMemo(() => {
    const last = meta.last_page;
    const cur = meta.current_page;
    if (last <= 9) {
      return Array.from({ length: last }, (_, i) => i + 1);
    }
    const set = new Set([1, last, cur, cur - 1, cur + 1, cur - 2, cur + 2]);
    return [...set].filter((n) => n >= 1 && n <= last).sort((a, b) => a - b);
  }, [meta.last_page, meta.current_page]);

  return (
    <div className="rwm-browse-jobs bg-white">
      <Container className="py-4">
        <Row className="g-4">
          <Col lg={3}>
            <div
              className="rwm-browse-jobs__filter-panel border rounded p-3 h-100"
              style={{ borderColor: 'var(--rwm-border)', background: 'var(--rwm-bg-light)' }}
              data-testid="rwm-filter-panel"
            >
              <h2 className="h5 fw-bold mb-3" style={{ color: 'var(--rwm-dark)' }}>
                Filter Jobs
              </h2>
              <Form.Group className="mb-3" controlId="rwm-filter-keyword-field">
                <Form.Label className="small text-muted">Keyword</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Job title or keyword"
                  value={draftKeyword}
                  onChange={(e) => setDraftKeyword(e.target.value)}
                  data-testid="rwm-filter-keyword"
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="rwm-filter-category-field">
                <Form.Label className="small text-muted">Category</Form.Label>
                <Form.Select
                  value={draftCategory}
                  onChange={(e) => setDraftCategory(e.target.value)}
                  data-testid="rwm-filter-category"
                >
                  <option value="">All categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.slug}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3" data-testid="rwm-filter-employment-type">
                <Form.Label className="small text-muted">Job Type</Form.Label>
                <div className="d-flex flex-column gap-1">
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-et-full-time"
                    label="Full Time"
                    checked={draftEmploymentTypes['full-time']}
                    onChange={() => toggleEmploymentType('full-time')}
                    data-testid="rwm-filter-et-full-time"
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-et-part-time"
                    label="Part Time"
                    checked={draftEmploymentTypes['part-time']}
                    onChange={() => toggleEmploymentType('part-time')}
                    data-testid="rwm-filter-et-part-time"
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-et-contract"
                    label="Contract"
                    checked={draftEmploymentTypes.contract}
                    onChange={() => toggleEmploymentType('contract')}
                    data-testid="rwm-filter-et-contract"
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-et-freelance"
                    label="Freelance"
                    checked={draftEmploymentTypes.freelance}
                    onChange={() => toggleEmploymentType('freelance')}
                    data-testid="rwm-filter-et-freelance"
                  />
                </div>
              </Form.Group>
              <Form.Group className="mb-3" data-testid="rwm-filter-salary">
                <Form.Label className="small text-muted">Minimum Salary</Form.Label>
                <div className="d-flex flex-column gap-1">
                  <Form.Check
                    type="radio"
                    name="rwm-filter-salary"
                    id="rwm-filter-salary-any"
                    label="Any salary"
                    checked={draftSalaryThreshold === ''}
                    onChange={() => setDraftSalaryThreshold('')}
                    data-testid="rwm-filter-salary-any"
                  />
                  <Form.Check
                    type="radio"
                    name="rwm-filter-salary"
                    id="rwm-filter-salary-50k"
                    label="€50,000+"
                    checked={draftSalaryThreshold === '50000'}
                    onChange={() => setDraftSalaryThreshold('50000')}
                    data-testid="rwm-filter-salary-50k"
                  />
                  <Form.Check
                    type="radio"
                    name="rwm-filter-salary"
                    id="rwm-filter-salary-75k"
                    label="€75,000+"
                    checked={draftSalaryThreshold === '75000'}
                    onChange={() => setDraftSalaryThreshold('75000')}
                    data-testid="rwm-filter-salary-75k"
                  />
                  <Form.Check
                    type="radio"
                    name="rwm-filter-salary"
                    id="rwm-filter-salary-100k"
                    label="€100,000+"
                    checked={draftSalaryThreshold === '100000'}
                    onChange={() => setDraftSalaryThreshold('100000')}
                    data-testid="rwm-filter-salary-100k"
                  />
                  <Form.Check
                    type="radio"
                    name="rwm-filter-salary"
                    id="rwm-filter-salary-150k"
                    label="€150,000+"
                    checked={draftSalaryThreshold === '150000'}
                    onChange={() => setDraftSalaryThreshold('150000')}
                    data-testid="rwm-filter-salary-150k"
                  />
                </div>
              </Form.Group>
              <Form.Group className="mb-3" data-testid="rwm-filter-seniority">
                <Form.Label className="small text-muted">Seniority Level</Form.Label>
                <div className="d-flex flex-column gap-1">
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-seniority-entry"
                    label="Entry Level"
                    checked={draftSenioritySelection.entry}
                    onChange={() => toggleSeniority('entry')}
                    data-testid="rwm-filter-seniority-entry"
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-seniority-mid"
                    label="Mid-Level"
                    checked={draftSenioritySelection.mid}
                    onChange={() => toggleSeniority('mid')}
                    data-testid="rwm-filter-seniority-mid"
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-seniority-senior"
                    label="Senior"
                    checked={draftSenioritySelection.senior}
                    onChange={() => toggleSeniority('senior')}
                    data-testid="rwm-filter-seniority-senior"
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-seniority-lead"
                    label="Lead / Principal"
                    checked={draftSenioritySelection.lead}
                    onChange={() => toggleSeniority('lead')}
                    data-testid="rwm-filter-seniority-lead"
                  />
                </div>
              </Form.Group>
              {Object.keys(skillsData).length > 0 ? (
                <Form.Group className="mb-3" data-testid="rwm-browse-filter-skills">
                  <Form.Label className="small text-muted">Skills</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search skills..."
                    value={skillSearch}
                    onChange={(e) => setSkillSearch(e.target.value)}
                    className="mb-2"
                    size="sm"
                    data-testid="rwm-browse-skill-search"
                  />
                  {draftSkills.length > 0 ? (
                    <div className="mb-2 d-flex align-items-center justify-content-between">
                      <span className="small text-muted" data-testid="rwm-browse-skills-count">
                        {draftSkills.length} selected
                      </span>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none"
                        onClick={() => setDraftSkills([])}
                        data-testid="rwm-browse-skills-clear"
                      >
                        Clear skills
                      </button>
                    </div>
                  ) : null}
                  <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                    {skillSearch.trim() ? (
                      Object.entries(skillsData)
                        .flatMap(([, categorySkills]) => categorySkills)
                        .filter((s) =>
                          s.name.toLowerCase().includes(skillSearch.trim().toLowerCase())
                        )
                        .map((skill) => (
                          <Form.Check
                            key={skill.id}
                            type="checkbox"
                            id={`rwm-browse-skill-${skill.id}`}
                            label={skill.name}
                            checked={draftSkills.includes(skill.id)}
                            onChange={() =>
                              setDraftSkills((prev) =>
                                prev.includes(skill.id)
                                  ? prev.filter((id) => id !== skill.id)
                                  : [...prev, skill.id]
                              )
                            }
                            className="small mb-1"
                            data-testid={`rwm-browse-skill-${skill.id}`}
                          />
                        ))
                    ) : (
                      Object.entries(skillsData).map(([category, categorySkills]) => (
                        <SkillCategoryGroup
                          key={category}
                          category={category}
                          skills={categorySkills}
                          selectedIds={draftSkills}
                          onToggle={(id) =>
                            setDraftSkills((prev) =>
                              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                            )
                          }
                        />
                      ))
                    )}
                  </div>
                </Form.Group>
              ) : null}
              <Form.Group className="mb-3" data-testid="rwm-filter-location">
                <Form.Label className="small text-muted">Hiring remotely from</Form.Label>
                <div className="d-flex flex-column gap-1">
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-location-worldwide"
                    label="Worldwide"
                    checked={draftLocationSelection.Worldwide}
                    onChange={() => toggleLocation('Worldwide')}
                    disabled={draftSpecificCountryMode}
                    data-testid={LOCATION_FILTER_TEST_IDS.Worldwide}
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-location-europe"
                    label="Europe only"
                    checked={draftLocationSelection['Europe only']}
                    onChange={() => toggleLocation('Europe only')}
                    disabled={draftSpecificCountryMode}
                    data-testid={LOCATION_FILTER_TEST_IDS['Europe only']}
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-location-us"
                    label="US only"
                    checked={draftLocationSelection['US only']}
                    onChange={() => toggleLocation('US only')}
                    disabled={draftSpecificCountryMode}
                    data-testid={LOCATION_FILTER_TEST_IDS['US only']}
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-location-americas"
                    label="Americas"
                    checked={draftLocationSelection.Americas}
                    onChange={() => toggleLocation('Americas')}
                    disabled={draftSpecificCountryMode}
                    data-testid={LOCATION_FILTER_TEST_IDS.Americas}
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-location-emea"
                    label="EMEA"
                    checked={draftLocationSelection.EMEA}
                    onChange={() => toggleLocation('EMEA')}
                    disabled={draftSpecificCountryMode}
                    data-testid={LOCATION_FILTER_TEST_IDS.EMEA}
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-location-apac"
                    label="APAC"
                    checked={draftLocationSelection.APAC}
                    onChange={() => toggleLocation('APAC')}
                    disabled={draftSpecificCountryMode}
                    data-testid={LOCATION_FILTER_TEST_IDS.APAC}
                  />
                  <Form.Check
                    type="checkbox"
                    id="rwm-filter-location-specific-country"
                    label="Specific country"
                    checked={draftSpecificCountryMode}
                    onChange={(e) => {
                      if (e.target.checked) {
                        enableSpecificCountryMode();
                      } else {
                        disableSpecificCountryMode();
                      }
                    }}
                    data-testid="rwm-filter-location-specific-country"
                  />
                </div>
                {draftSpecificCountryMode ? (
                  <div className="mt-2">
                    <CountrySearchSelect
                      countries={countries}
                      loading={countriesLoading}
                      value={draftSelectedCountry}
                      onChange={changeDraftCountry}
                    />
                  </div>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3" data-testid="rwm-filter-posted">
                <Form.Label className="small text-muted">Posted Date</Form.Label>
                <div className="d-flex flex-column gap-1">
                  <Form.Check
                    type="radio"
                    name="rwm-filter-posted"
                    id="rwm-filter-posted-any"
                    label="Any time"
                    checked={draftPostedPreset === ''}
                    onChange={() => changePostedPreset('')}
                    data-testid="rwm-filter-posted-any"
                  />
                  <Form.Check
                    type="radio"
                    name="rwm-filter-posted"
                    id="rwm-filter-posted-24h"
                    label="Last 24 hours"
                    checked={draftPostedPreset === '24h'}
                    onChange={() => changePostedPreset('24h')}
                    data-testid="rwm-filter-posted-24h"
                  />
                  <Form.Check
                    type="radio"
                    name="rwm-filter-posted"
                    id="rwm-filter-posted-7d"
                    label="Last week"
                    checked={draftPostedPreset === '7d'}
                    onChange={() => changePostedPreset('7d')}
                    data-testid="rwm-filter-posted-7d"
                  />
                  <Form.Check
                    type="radio"
                    name="rwm-filter-posted"
                    id="rwm-filter-posted-30d"
                    label="Last month"
                    checked={draftPostedPreset === '30d'}
                    onChange={() => changePostedPreset('30d')}
                    data-testid="rwm-filter-posted-30d"
                  />
                  <Form.Check
                    type="radio"
                    name="rwm-filter-posted"
                    id="rwm-filter-posted-90d"
                    label="Last 3 months"
                    checked={draftPostedPreset === '90d'}
                    onChange={() => changePostedPreset('90d')}
                    data-testid="rwm-filter-posted-90d"
                  />
                  <Form.Check
                    type="radio"
                    name="rwm-filter-posted"
                    id="rwm-filter-posted-custom"
                    label="Custom range"
                    checked={draftPostedPreset === 'custom'}
                    onChange={() => changePostedPreset('custom')}
                    data-testid="rwm-filter-posted-custom"
                  />
                </div>
                {draftPostedPreset === 'custom' && (
                  <div className="mt-2 d-flex flex-column gap-2">
                    <Form.Group controlId="rwm-filter-date-from-field">
                      <Form.Label className="small text-muted mb-0">From</Form.Label>
                      <Form.Control
                        type="date"
                        value={draftCustomDateFrom}
                        onChange={(e) => {
                          setPostedDateError('');
                          setDraftCustomDateFrom(e.target.value);
                        }}
                        data-testid="rwm-filter-date-from"
                      />
                    </Form.Group>
                    <Form.Group controlId="rwm-filter-date-to-field">
                      <Form.Label className="small text-muted mb-0">To</Form.Label>
                      <Form.Control
                        type="date"
                        value={draftCustomDateTo}
                        onChange={(e) => {
                          setPostedDateError('');
                          setDraftCustomDateTo(e.target.value);
                        }}
                        data-testid="rwm-filter-date-to"
                      />
                    </Form.Group>
                  </div>
                )}
                {postedDateError ? (
                  <p className="text-danger small mb-0 mt-2" role="alert">
                    {postedDateError}
                  </p>
                ) : null}
              </Form.Group>
              <Button
                variant="info"
                className="w-100 text-white"
                type="button"
                onClick={handleApply}
                data-testid="rwm-filter-apply"
              >
                Apply Filters
              </Button>
              <div className="text-center mt-2">
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 rwm-browse-jobs__clear"
                  onClick={handleClear}
                  data-testid="rwm-filter-clear"
                >
                  Clear
                </button>
              </div>
              {isCandidate && hasActiveFilters ? (
                <div className="mt-3 pt-3 border-top">
                  {!showSaveSearch ? (
                    <Button
                      variant="outline-info"
                      size="sm"
                      className="w-100"
                      type="button"
                      onClick={() => {
                        setSaveSearchError('');
                        setSaveSearchSuccess(false);
                        setShowSaveSearch(true);
                      }}
                      data-testid="rwm-save-search-btn"
                    >
                      Save this search
                    </Button>
                  ) : (
                    <div>
                      <Form.Group controlId="rwm-save-search-name" className="mb-2">
                        <Form.Label className="small text-muted mb-1">Search name</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          maxLength={50}
                          value={saveSearchName}
                          onChange={(e) => setSaveSearchName(e.target.value)}
                          data-testid="rwm-save-search-name-input"
                        />
                      </Form.Group>
                      <Button
                        variant="info"
                        size="sm"
                        className="w-100 text-white"
                        type="button"
                        disabled={saveSearchSaving || !saveSearchName.trim()}
                        onClick={handleSaveSearch}
                        data-testid="rwm-save-search-confirm"
                      >
                        {saveSearchSaving ? 'Saving…' : 'Save'}
                      </Button>
                    </div>
                  )}
                  {saveSearchError ? (
                    <p className="text-danger small mb-0 mt-2" role="alert">
                      {saveSearchError}
                    </p>
                  ) : null}
                  {saveSearchSuccess ? (
                    <p className="text-success small mb-0 mt-2">Search saved.</p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </Col>
          <Col lg={9}>
            <div ref={jobListRef}>
              <p
                className="fw-medium mb-3"
                style={{ color: 'var(--rwm-dark)' }}
                data-testid="rwm-results-count"
              >
                {loading ? 'Loading…' : resultsLine()}
              </p>

            {loading && (
              <div>
                {[1, 2, 3, 4].map((k) => (
                  <div key={k} className="rwm-browse-jobs__skeleton-card mb-3" data-testid="rwm-skeleton-card">
                    <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--lg mb-2" />
                    <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--md mb-3" />
                    <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--sm" />
                  </div>
                ))}
              </div>
            )}

            {!loading && error && (
              <p className="text-muted">Could not load jobs. Please try again later.</p>
            )}

            {!loading && !error && jobs.length === 0 && (
              <div className="text-center py-5" data-testid="rwm-jobs-empty">
                <p className="text-muted mb-0">No remote jobs found. Try different filters.</p>
              </div>
            )}

            {!loading && !error && jobs.length > 0 && (
              <div>
                {jobs.map((job) => {
                  const featured = Boolean(job.is_featured);
                  const company = job.company || {};
                  const category = job.category || {};
                  const logoUrl = company.logo_url || null;
                  const initial = (company.name && company.name.trim()[0]) || '?';
                  const salaryText = formatSalaryBrowse(
                    job.salary_min,
                    job.salary_max,
                    job.salary_currency,
                    job.salary_period
                  );
                  const cardClass = `rwm-job-card border rounded p-3 mb-3 ${featured ? 'rwm-job-card--featured' : ''}`;
                  const seniorityLevels = Array.isArray(job.seniority_level) ? job.seniority_level : [];

                  return (
                    <div
                      key={job.id}
                      className="rwm-job-card-link"
                      data-testid={`rwm-job-card-link-${job.id}`}
                      onClick={() => navigate(`/jobs/${job.slug}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/jobs/${job.slug}`);
                        }
                      }}
                      role="link"
                      tabIndex={0}
                    >
                    <div className={cardClass} data-testid="rwm-job-card">
                      <Row className="align-items-start g-3">
                        <Col xs="auto">
                          <div style={{ width: 48, height: 48 }}>
                            {logoUrl ? (
                              <img
                                src={logoUrl}
                                alt={company.name || 'Company'}
                                width={48}
                                height={48}
                                className="rounded"
                                style={{ objectFit: 'cover', width: 48, height: 48 }}
                              />
                            ) : (
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-semibold"
                                style={{
                                  width: 48,
                                  height: 48,
                                  backgroundColor: avatarColor(company.name || ''),
                                  fontSize: 18,
                                }}
                              >
                                {initial.toUpperCase()}
                              </div>
                            )}
                          </div>
                        </Col>
                        <Col>
                          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                            <div>
                              <Link
                                to={`/jobs/${job.slug}`}
                                className="rwm-browse-jobs__title-link fw-semibold text-decoration-none h5 mb-1 d-inline-block"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {job.title}
                              </Link>
                              <div className="small" style={{ color: 'var(--rwm-slate)' }}>
                                {company.name}
                                {category.name ? ` · ${category.name}` : ''}
                              </div>
                            </div>
                            <div className="d-flex flex-column align-items-end gap-1">
                              {featured && (
                                <span
                                  className="badge rounded-pill rwm-browse-jobs__featured-badge"
                                  data-testid="rwm-featured-badge"
                                >
                                  Featured
                                </span>
                              )}
                              <span
                                className="badge rounded-pill rwm-salary-badge"
                                data-testid="rwm-salary-badge"
                              >
                                {salaryText}
                              </span>
                              <span className="small" style={{ color: 'var(--rwm-slate)' }}>
                                {timeAgo(job.approved_at)}
                              </span>
                            </div>
                          </div>
                          <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                            {job.employment_type ? (
                              <JobTag
                                type="employment"
                                value={job.employment_type}
                                className="badge rounded-pill rwm-browse-jobs__employment-badge"
                              >
                                {formatEmploymentLabel(job.employment_type)}
                              </JobTag>
                            ) : null}
                            {job.location_country ? (
                              <JobTag
                                type="country"
                                value={job.location_country}
                                className="badge rounded-pill rwm-browse-jobs__employment-badge"
                              >
                                {job.location_country}
                              </JobTag>
                            ) : job.location_eligibility != null && job.location_eligibility !== '' ? (
                              <JobTag
                                type="location"
                                value={job.location_eligibility}
                                className="badge rounded-pill rwm-browse-jobs__employment-badge"
                              >
                                {job.location_eligibility}
                              </JobTag>
                            ) : null}
                            {seniorityLevels.length > 0 ? (
                              <JobTag
                                type="seniority"
                                value={seniorityLevels.join(',')}
                                className="badge rounded-pill rwm-browse-jobs__employment-badge"
                              >
                                {formatSeniorityTags(job.seniority_level)}
                              </JobTag>
                            ) : null}
                            <Button
                              variant="primary"
                              size="sm"
                              data-testid="rwm-job-apply"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/jobs/${job.slug}`);
                              }}
                            >
                              Apply Now
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                    </div>
                  );
                })}

                {meta.last_page > 1 && (
                  <nav
                    className="d-flex flex-wrap align-items-center justify-content-center gap-2 mt-4"
                    aria-label="Pagination"
                    data-testid="rwm-pagination"
                  >
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={meta.current_page <= 1}
                      onClick={() => goToPage(meta.current_page - 1)}
                      data-testid="rwm-pagination-prev"
                    >
                      Prev
                    </Button>
                    {pageNumbers.map((n, idx) => (
                      <React.Fragment key={n}>
                        {idx > 0 && pageNumbers[idx - 1] !== n - 1 && (
                          <span className="text-muted px-1">…</span>
                        )}
                        <Button
                          variant={n === meta.current_page ? 'primary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => goToPage(n)}
                        >
                          {n}
                        </Button>
                      </React.Fragment>
                    ))}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={meta.current_page >= meta.last_page}
                      onClick={() => goToPage(meta.current_page + 1)}
                      data-testid="rwm-pagination-next"
                    >
                      Next
                    </Button>
                  </nav>
                )}
              </div>
            )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default BrowseJobs;
